/**
 * Traversal-grammar compiler (TDD §4.1). Pure: 3Cs profile -> the measurable grammar
 * every geometry/tether check reads. The fixed-impulse jump arc is the master unit.
 */
import type { ThreeCsProfile } from './schema.js';

export const DEFAULT_COLLAPSE_SAFETY_MARGIN = 0.15;
export const DEFAULT_CLEARANCE_MARGIN = 0.1;

export interface TraversalGrammar {
  gravity: number; // 2*jumpHeight / (jumpAirTime/2)^2
  jumpImpulse: number; // v0 = gravity*(jumpAirTime/2) = 4*jumpHeight/jumpAirTime
  maxJumpGap: number; // autoRunSpeed*jumpAirTime (range at Δy=0)
  maxRise: number; // jumpHeight (arc apex)
  minGroupSpread: number; // (members-1)*bodyWidth
  maxGroupSpread: number; // (members-1)*maxStretch
  minGroupFootprint: number; // members*bodyWidth
  closeRate: number; // autoRunSpeed - backpedalSpeed
  draggedGroupSpeed: number; // autoRunSpeed*(1 - downedDragSpeedPenalty)
  collapseSafetyMargin: number;
  clearanceMargin: number;
  // echoed inputs the helpers need:
  autoRunSpeed: number;
  backpedalSpeed: number;
  bodyWidth: number;
}

/**
 * A level's effective auto-run speed given the optional pace ramp and its 0-based play
 * position. No pace, or index 0/unknown → the flat base. Pure; clamps at `maxSpeed`.
 */
export function effectiveAutoRunSpeed(p: ThreeCsProfile, levelIndex?: number): number {
  const base = p.character.autoRunSpeed;
  const pace = p.pace;
  if (!pace || !levelIndex || levelIndex <= 0) return base;
  return Math.min(pace.maxSpeed, base + levelIndex * pace.escalationPerLevel);
}

export function compileTraversalGrammar(
  p: ThreeCsProfile,
  /** Overrides the base autoRunSpeed for the speed-derived terms — pass a level's
   *  effective (pace-ramped) speed so its geometry is checked at the speed it runs at.
   *  Omit for the flat base (back-compat). */
  autoRunSpeedOverride?: number,
): TraversalGrammar {
  const {
    autoRunSpeed: baseAutoRunSpeed,
    backpedalSpeed,
    jumpHeight,
    jumpAirTime,
    downedDragSpeedPenalty,
    bodyWidth,
  } = p.character;
  const autoRunSpeed =
    autoRunSpeedOverride !== undefined && autoRunSpeedOverride > 0
      ? autoRunSpeedOverride
      : baseAutoRunSpeed;
  // Untethered/solo (tether optional, design §17): one body, zero spread — the
  // group-solvability terms collapse to solo semantics and their checks pass trivially.
  const { members, maxStretch } = p.tether ?? { members: 1, maxStretch: 0 };
  const tUp = jumpAirTime / 2;
  const gravity = (2 * jumpHeight) / (tUp * tUp);
  const jumpImpulse = gravity * tUp;
  return {
    gravity,
    jumpImpulse,
    maxJumpGap: autoRunSpeed * jumpAirTime,
    maxRise: jumpHeight,
    minGroupSpread: (members - 1) * bodyWidth,
    maxGroupSpread: (members - 1) * maxStretch,
    minGroupFootprint: members * bodyWidth,
    closeRate: autoRunSpeed - backpedalSpeed,
    draggedGroupSpeed: autoRunSpeed * (1 - downedDragSpeedPenalty),
    collapseSafetyMargin: p.solver?.collapseSafetyMargin ?? DEFAULT_COLLAPSE_SAFETY_MARGIN,
    clearanceMargin: p.solver?.clearanceMargin ?? DEFAULT_CLEARANCE_MARGIN,
    autoRunSpeed,
    backpedalSpeed,
    bodyWidth,
  };
}

/** Arc height (m) reached at horizontal distance d (m) for the fixed-impulse jump. */
export function jumpArcHeight(g: TraversalGrammar, d: number): number {
  const t = d / g.autoRunSpeed;
  return g.jumpImpulse * t - 0.5 * g.gravity * t * t;
}

/** Horizontal width over which the arc stays >= h (0 if h > apex). */
export function arcWidthAboveHeight(g: TraversalGrammar, h: number): number {
  return 2 * g.autoRunSpeed * Math.sqrt(Math.max(0, (2 * (g.maxRise - h)) / g.gravity));
}

/**
 * Horizontal distance (m) at which the RISING arc first reaches height h — the minimum
 * run-up a fence of height h needs, since the runner must already be at h by the fence's
 * near edge (the fence WIDTH is bounded separately by `arcWidthAboveHeight`). 0 for h<=0;
 * Infinity if h exceeds the apex (never reached — caught earlier as `fence-too-tall`).
 * Correct for LOW fences, which the old "center the apex" heuristic over-charged.
 */
export function horizontalDistToHeight(g: TraversalGrammar, h: number): number {
  if (h <= 0) return 0;
  const disc = g.jumpImpulse * g.jumpImpulse - 2 * g.gravity * h;
  if (disc < 0) return Number.POSITIVE_INFINITY;
  const t = (g.jumpImpulse - Math.sqrt(disc)) / g.gravity; // smaller (rising) root
  return g.autoRunSpeed * t;
}

/** When `depth` is not authored, a gap consuming ≥ this fraction of the clearable
 *  hop reads as a commit-or-fall DEEP shaft; narrower gaps are a SHALLOW pocket. */
export const DEEP_GAP_FRACTION = 0.6;

/**
 * Classify a `gap` segment's pit character. The authored `depth` wins; when absent,
 * derive it DETERMINISTICALLY from span vs the jump reach (`maxJumpGap`) — a gap that
 * eats most of a clearable hop is a fall-into DEEP shaft (escape by wall-kick), a
 * comfortably-hoppable one is a SHALLOW pocket. This is the single source of truth
 * both the previs and build agents should use, so the pit reads the same everywhere.
 * Depth is NEVER decided from the level's name or prose (that is a classification smell).
 */
export function gapDepth(
  seg: { span: number; depth?: 'shallow' | 'deep' },
  g: TraversalGrammar,
): 'shallow' | 'deep' {
  if (seg.depth) return seg.depth;
  return seg.span >= DEEP_GAP_FRACTION * g.maxJumpGap ? 'deep' : 'shallow';
}
